import { useEffect, useRef, useState } from "react";
import { BASE_URL } from "../../config";
import { showSuccess, showError } from "../../utils/dialog";
import { getAuthItem } from "../../utils/auth";
import { formatAppDateTime } from "../../utils/appDateTime";
import UnsavedChangesModal from "../UnsavedChangesModal";
import DateTimePickerPanel from "../DateTimePicker";

const AddAdjustmentMoal = ({ isOpen, onClose, onPayerAdded }) => {
  const [quantity, setQuantity] = useState("");
  const [rate, setRate] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [subjectQuery, setSubjectQuery] = useState("");
  const [showSubjectSuggestions, setShowSubjectSuggestions] = useState(false);
  const [recognitionDate, setRecognitionDate] = useState("");
  const [description, setDescription] = useState("");
  const [note, setNote] = useState("");
  const [addHst, setAddHst] = useState("no");
  const [fieldErrors, setFieldErrors] = useState({});
  const [saveError, setSaveError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showUnsavedPopup, setShowUnsavedPopup] = useState(false);
  const [showValidationAlert, setShowValidationAlert] = useState(false);
  const [showRecognitionPicker, setShowRecognitionPicker] = useState(false);
  const [recognitionWeekState, setRecognitionWeekState] = useState("");
  const validationTimerRef = useRef(null);

  const normalizeDecimalInput = (value) => String(value ?? "").replace(/\u2212/g, "-");
  const isDecimalInputDraft = (value) =>
    /^-?(?:\d+)?(?:\.\d*)?$/.test(normalizeDecimalInput(value));
  const parseDecimalInput = (value) => {
    const raw = normalizeDecimalInput(value).trim();
    if (!raw || raw === "-" || raw === "." || raw === "-.") return NaN;
    return Number(raw);
  };
  const quantityNumber = parseDecimalInput(quantity);
  const rateNumber = parseDecimalInput(rate);
  const total =
    quantity !== "" &&
    rate !== "" &&
    Number.isFinite(quantityNumber) &&
    Number.isFinite(rateNumber)
      ? quantityNumber * rateNumber
      : "";
  const subjectSuggestions = (() => {
    const q = String(subjectQuery || "").trim().toLowerCase();
    const list = Array.isArray(subjects) ? subjects : [];

    if (!q) {
      const staff = list.filter((s) => s.type === "staff").slice(0, 8);
      const clients = list.filter((s) => s.type === "client").slice(0, 8);
      return [...staff, ...clients];
    }

    return list
      .filter((subject) => {
        const hay =
          `${subject.name || ""} ${subject.type || ""} ${subject.id ?? ""}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 20);
  })();

  useEffect(() => {
    if (!isOpen) {
      setQuantity("");
      setRate("");
      setSelectedSubject("");
      setSubjectQuery("");
      setRecognitionDate("");
      setDescription("");
      setNote("");
      setAddHst("no");
      setFieldErrors({});
      setSaveError("");
      setShowSubjectSuggestions(false);
      setIsDirty(false);
      setShowUnsavedPopup(false);
      setShowValidationAlert(false);
      setShowRecognitionPicker(false);
      setRecognitionWeekState("");
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !isDirty) return undefined;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty, isOpen]);

  useEffect(() => {
    return () => {
      if (validationTimerRef.current) {
        clearTimeout(validationTimerRef.current);
      }
    };
  }, []);

  const handleLoadSubjects = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/accounting/staff-clients`);
      if (!res.ok) {
        throw new Error("Failed to fetch subjects");
      }
      const data = await res.json();
      const list = Array.isArray(data?.data) ? data.data : [];
      setSubjects(list);
    } catch (error) {
      console.error("Error loading subjects:", error);
    }
  };

  useEffect(() => {
    handleLoadSubjects();
  }, []);

  const formatDateTimeForApi = (value) => {
    if (!value) return "";
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  const formatRecognitionDisplay = (value) =>
    formatAppDateTime(value, { emptyValue: "" });

  const triggerValidationAlert = () => {
    setShowValidationAlert(true);
    if (validationTimerRef.current) {
      clearTimeout(validationTimerRef.current);
    }
    validationTimerRef.current = setTimeout(() => {
      setShowValidationAlert(false);
      validationTimerRef.current = null;
    }, 4000);
  };

  const toIsoDate = (date) => {
    if (!date || Number.isNaN(date.getTime())) return "";
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const getWeekStartIso = (value) => {
    if (!value) return "";
    const date = value instanceof Date ? new Date(value) : new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const day = date.getDay(); // 0=Sun ... 6=Sat
    const diffToMonday = day === 0 ? -6 : 1 - day;
    date.setDate(date.getDate() + diffToMonday);
    return toIsoDate(date);
  };

  const fetchWeekStateByRecognitionDate = async (value) => {
    const weekStart = getWeekStartIso(value);
    if (!weekStart) return "";
    try {
      const res = await fetch(
        `${BASE_URL}/api/accounting/weeks?startDate=${encodeURIComponent(weekStart)}`,
        {
          credentials: "include",
        },
      );
      if (!res.ok) return "";
      const json = await res.json().catch(() => ({}));
      return String(json?.data?.state || "").toLowerCase();
    } catch (error) {
      console.error("Failed to validate recognition week state", error);
      return "";
    }
  };

  useEffect(() => {
    const syncRecognitionWeekState = async () => {
      if (!recognitionDate) {
        setRecognitionWeekState("");
        return;
      }
      const state = await fetchWeekStateByRecognitionDate(recognitionDate);
      setRecognitionWeekState(state);
    };
    syncRecognitionWeekState();
  }, [recognitionDate]);

  if (!isOpen) return null;

  const validateForm = () => {
    const errors = {};
    if (!selectedSubject) errors.subject_id = "This field is required";
    if (!recognitionDate) errors.recognition_date = "This field is required";

    const qtyRaw = normalizeDecimalInput(quantity).trim();
    const rateRaw = normalizeDecimalInput(rate).trim();
    const qtyNum = parseDecimalInput(qtyRaw);
    const rateNum = parseDecimalInput(rateRaw);

    if (!qtyRaw) errors.quantity = "This field is required";
    else if (!Number.isFinite(qtyNum)) {
      errors.quantity = "Please enter a valid number";
    } else if (qtyNum <= 0) {
      errors.quantity = "Must be greater than 0";
    }

    if (!rateRaw) errors.rate = "This field is required";
    else if (!Number.isFinite(rateNum)) {
      errors.rate = "Please enter a valid number";
    } else if (rateNum === 0) {
      errors.rate = "Cannot be zero";
    }

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      triggerValidationAlert();
      return false;
    }
    setShowValidationAlert(false);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveError("");

    if (!validateForm()) {
      return;
    }

    const dateWeekState =
      recognitionWeekState || (await fetchWeekStateByRecognitionDate(recognitionDate));
    if (dateWeekState === "closed") {
      const msg =
        "Recognition Date falls in a closed week; adjustments cannot be created";
      setFieldErrors((prev) => ({ ...prev, recognition_date: msg }));
      setSaveError(msg);
      showError(msg);
      return;
    }

    const selected = subjects.find(
      (s) => `${s.kind || (s.type === "client" ? "client" : "account")}:${s.id}` === selectedSubject,
    );

    if (!selected) {
      setFieldErrors((prev) => ({ ...prev, subject_id: "Invalid subject" }));
      return;
    }

    const qtyNum = parseDecimalInput(quantity);
    const rateNum = parseDecimalInput(rate);

    const payload = {
      subject_id: selected.id,
      subject_type:
        (selected.kind || "").toLowerCase() === "client"
          ? "client"
          : String(selected.type || "staff").toLowerCase(),
      description,
      note,
      recognition_date: formatDateTimeForApi(recognitionDate),
      quantity: qtyNum,
      rate: rateNum,
      add_hst: addHst === "yes" ? 1 : 0,
    };

    try {
      setIsSaving(true);
      const token = getAuthItem("token");
      const accountId = getAuthItem("accountId");
      const res = await fetch(`${BASE_URL}/api/accounting/adjustments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(accountId ? { "X-Author-Account-Id": String(accountId) } : {}),
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        if (data?.errors && typeof data.errors === "object") {
          setFieldErrors((prev) => ({ ...prev, ...data.errors }));
        }
        throw new Error(data.message || "Failed to create adjustment");
      }

      showSuccess("Adjustment created successfully");
      setIsDirty(false);
      if (typeof onPayerAdded === "function") onPayerAdded();
      else onClose();
    } catch (error) {
      console.error(error);
      setSaveError(error?.message || "Error creating adjustment");
      showError(error?.message || "Error creating adjustment");
    } finally {
      setIsSaving(false);
    }
  };

  const handleAttemptClose = () => {
    if (isSaving) return;
    if (isDirty) {
      setShowUnsavedPopup(true);
      return;
    }
    onClose();
  };

  const handleDiscardChanges = () => {
    setShowUnsavedPopup(false);
    setIsDirty(false);
    onClose();
  };

  const handleReviewChanges = () => {
    setShowUnsavedPopup(false);
  };

  const handleSaveAndContinue = async () => {
    setShowUnsavedPopup(false);
    const event = { preventDefault() {} };
    await handleSubmit(event);
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          handleAttemptClose();
        }
      }}
    >
      <div
        className="bg-white rounded-xl shadow-lg w-full max-w-3xl p-6 relative"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-[#DC1F00] hover:bg-[#DC1F0033] p-3 rounded-full"
        >
          ✖
        </button>
        <h2 className="text-xl text-[#3A4149] font-semibold mb-4">
          Add Adjustment
        </h2>
        <div className="h-[1px] bg-[#a15d33]"></div>

        <form
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto p-6 space-y-4"
        >
          {recognitionWeekState === "closed" && (
            <div className="mb-3 p-3 bg-yellow-100 border-l-4 border-yellow-400 text-yellow-800">
              Recognition Date falls in a closed week; new adjustments are disabled.
            </div>
          )}
          <div className="grid grid-cols-2 gap-8">
            <div>
              <h3 className="font-semibold text-[#000000] mb-2">
                General Information
              </h3>
              <label className="block text-sm text-[#00000080] mb-2">
                Subject (client or Staff){" "}
                <span className="text-[#00000080]">*</span>
              </label>
              <div className="relative mb-2">
                <input
                  type="text"
                  value={subjectQuery}
                  placeholder="Search subject..."
                  onFocus={() => setShowSubjectSuggestions(true)}
                  onBlur={() =>
                    setTimeout(() => setShowSubjectSuggestions(false), 150)
                  }
                  onChange={(e) => {
                    setIsDirty(true);
                    setSubjectQuery(e.target.value);
                    setSelectedSubject("");
                    if (fieldErrors.subject_id) {
                      setFieldErrors((prev) => ({ ...prev, subject_id: "" }));
                    }
                  }}
                  className={`w-full border rounded-lg px-3 py-2 pr-8 focus:ring-1 focus:ring-red-400 ${fieldErrors.subject_id ? "border-red-500" : ""}`}
                />
                {subjectQuery && (
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    onClick={() => {
                      setIsDirty(true);
                      setSubjectQuery("");
                      setSelectedSubject("");
                      setShowSubjectSuggestions(false);
                    }}
                    aria-label="Clear subject"
                  >
                    x
                  </button>
                )}
                {showSubjectSuggestions && subjectSuggestions.length > 0 && (
                  <div className="absolute z-50 left-0 right-0 mt-1 max-h-48 overflow-y-auto rounded-lg border bg-white shadow">
                    {subjectSuggestions.map((subject) => (
                      <button
                        key={`${subject.type}-${subject.id}`}
                        type="button"
                        className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          const subjectKind =
                            subject.kind ||
                            (String(subject.type || "").toLowerCase() === "client"
                              ? "client"
                              : "account");
                          setSelectedSubject(`${subjectKind}:${subject.id}`);
                          setSubjectQuery(
                            `${subject.name} : ${subject.type || "subject"}`,
                          );
                          setIsDirty(true);
                          setShowSubjectSuggestions(false);
                          setFieldErrors((prev) => ({ ...prev, subject_id: "" }));
                        }}
                      >
                        {subject.id} — {subject.name} : {subject.type}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {fieldErrors.subject_id && (
                <p className="text-xs text-red-600 mb-2">{fieldErrors.subject_id}</p>
              )}

              <label className="block text-sm text-[#00000080] mb-2">
                Recognition Date <span className="text-[#00000080]">*</span>
              </label>
              <input
                type="text"
                readOnly
                value={formatRecognitionDisplay(recognitionDate)}
                onClick={() => setShowRecognitionPicker(true)}
                className={`w-full border rounded-lg px-3 py-2 mb-2 ${fieldErrors.recognition_date ? "border-red-500" : ""}`}
                placeholder="Select recognition date & time"
              />
              {fieldErrors.recognition_date && (
                <p className="text-xs text-red-600 mb-2">
                  {fieldErrors.recognition_date}
                </p>
              )}
              <label className="block text-sm text-[#00000080] mb-2">
                Line Item Description
              </label>
              <input
                type="text"
                value={description}
                onChange={(e) => {
                  setIsDirty(true);
                  setDescription(e.target.value);
                }}
                className="w-full border rounded-lg px-3 py-2 mb-4"
              />
              <label className="block text-sm text-[#00000080] mb-2">
                Note
              </label>
              <input
                type="text"
                value={note}
                onChange={(e) => {
                  setIsDirty(true);
                  setNote(e.target.value);
                }}
                className="w-full border rounded-lg px-3 py-2 mb-4 focus:ring-1 focus:ring-red-400"
              />
            </div>
            <div>
              <h3 className="font-semibold text-[#000000] mb-2">Amount</h3>
              <label className="block text-sm text-[#00000080] mb-2">
                Quantity <span className="text-[#00000080]">*</span>
              </label>
              <input
                type="text"
                inputMode="decimal"
                value={quantity}
                onChange={(e) => {
                  const value = normalizeDecimalInput(e.target.value);
                  if (isDecimalInputDraft(value)) {
                    setIsDirty(true);
                    setQuantity(value);
                    if (fieldErrors.quantity) {
                      setFieldErrors((prev) => ({ ...prev, quantity: "" }));
                    }
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "e" || e.key === "E" || e.key === "+") {
                    e.preventDefault(); // block exponent and plus
                  }
                }}
                className={`w-full border rounded-lg px-3 py-2 mb-2 focus:ring-1 focus:ring-red-400 ${fieldErrors.quantity ? "border-red-500" : ""}`}
              />
              {fieldErrors.quantity && (
                <p className="text-xs text-red-600 mb-2">{fieldErrors.quantity}</p>
              )}
              <label className="block text-sm text-[#00000080] mb-2">
                Rate <span className="text-[#00000080]">*</span>
              </label>
              <input
                type="text"
                inputMode="decimal"
                value={rate}
                onChange={(e) => {
                  const value = normalizeDecimalInput(e.target.value);
                  if (isDecimalInputDraft(value)) {
                    setIsDirty(true);
                    setRate(value);
                    if (fieldErrors.rate) {
                      setFieldErrors((prev) => ({ ...prev, rate: "" }));
                    }
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "e" || e.key === "E" || e.key === "+") {
                    e.preventDefault();
                  }
                }}
                className={`w-full border rounded-lg px-3 py-2 mb-2 focus:ring-1 focus:ring-red-400 ${fieldErrors.rate ? "border-red-500" : ""}`}
              />
              {fieldErrors.rate && (
                <p className="text-xs text-red-600 mb-2">{fieldErrors.rate}</p>
              )}
              <label className="block text-sm text-[#00000080] mb-2">
                Total <span className="text-[#00000080]">*</span>
              </label>
              <input
                type="text"
                value={total !== "" ? `$ ${total}` : ""}
                readOnly
                className="w-full border rounded-lg px-3 py-2 mb-4 focus:ring-1 focus:ring-red-400"
              />
              <label className="block text-sm text-[#00000080] mb-2">
                Add HST
              </label>
              <div className="flex items-center gap-8">
                <label className="flex items-center gap-1 text-sm text-[#00000080]">
                  <input
                    type="radio"
                    name="addHst"
                    value="no"
                    checked={addHst === "no"}
                    onChange={(e) => {
                      setIsDirty(true);
                      setAddHst(e.target.value);
                    }}
                  />
                  no
                </label>
                <label className="flex items-center gap-1 text-sm text-[#00000080]">
                  <input
                    type="radio"
                    name="addHst"
                    value="yes"
                    checked={addHst === "yes"}
                    onChange={(e) => {
                      setIsDirty(true);
                      setAddHst(e.target.value);
                    }}
                  />
                  yes
                </label>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between gap-8">
            <button
              onClick={onClose}
              className="w-full border-2 border-buttonWhite hover:bg-buttonWhite-hover text-[#DC1F00] px-4 py-2 rounded-full"
            >
              Close
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="w-full bg-buttonRed hover:bg-buttonRed-hover disabled:opacity-60 text-white px-4 py-2 rounded-full"
            >
              {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
          {saveError && <p className="text-sm text-red-600">{saveError}</p>}
        </form>
        <UnsavedChangesModal
          open={showUnsavedPopup}
          onSave={handleSaveAndContinue}
          onDiscard={handleDiscardChanges}
          onReview={handleReviewChanges}
          saving={isSaving}
        />
        {showRecognitionPicker && (
          <DateTimePickerPanel
            value={recognitionDate}
            onChange={(date) => {
              setIsDirty(true);
              setRecognitionDate(date);
              if (fieldErrors.recognition_date) {
                setFieldErrors((prev) => ({
                  ...prev,
                  recognition_date: "",
                }));
              }
            }}
            onDone={() => setShowRecognitionPicker(false)}
            onBack={() => setShowRecognitionPicker(false)}
          />
        )}
        {showValidationAlert && (
          <div className="fixed bottom-5 right-5 z-[120] rounded bg-[#f47d6b] px-5 py-4 text-sm font-medium text-white shadow-xl">
            Please review errors highlighted in red
          </div>
        )}
      </div>
    </div>
  );
};

export default AddAdjustmentMoal;
